import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { Box, Card, Container, Section, TabNav, Flex } from '@radix-ui/themes';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { instance } from 'api/axios.instance';
import { localList } from 'app/constants';
import { selectUserSelectedLocalCode, setUserSelectedLocal, selectDevice } from 'store/slices/common';

import Visual from 'components/common/visual';
import Breadcrumbs from 'components/common/breadcrumbs';
import PageTitle from 'components/layout/pageTitle';
import LocalSort from 'components/common/localSort';
import CenterIntroduction from 'components/localCenter/CenterIntroduction';
import OrganizationChart from 'components/localCenter/OrganizationChart';
import MapDirections from 'components/localCenter/MapDirections';
import NoData from 'components/common/noData';
import Loading from 'components/common/loading';

import './style.scss';

// 소개 > 지역센터소개 페이지
export default function LocalCenter() {
  const deviceType = useSelector(selectDevice);
  const isMobile = deviceType === 'mobile';

  // hooks
  const userSelectedLocalCode = useSelector(selectUserSelectedLocalCode);
  const dispatch = useDispatch();

  // state
  const [localData, setLocalData] = useState(null); // undefined 대신 null로 초기화
  const [loading, setLoading] = useState(false);
  const pathname = window.location.pathname.toLowerCase();
  const baseURL = process.env.REACT_APP_API_BASE;

  //tab
  const tabs = [
    { path: '/localcenter', label: '센터소개', to: '/localCenter' },
    { path: '/localorganization', label: '조직도', to: '/localOrganization' },
    { path: '/localmap', label: '찾아오시는 길', to: '/localMap' }
  ];

  // 지역 선택 method - 에러 핸들링 추가
  const setRegion = useCallback(
    region => {
      try {
        const selectedLocal = localList.find(local => local.code === region);
        if (selectedLocal) {
          dispatch(setUserSelectedLocal(selectedLocal.name));
        }
      } catch (error) {
        console.error('Error setting region:', error);
      }
    },
    [dispatch]
  );

  // api 조회 - 에러 핸들링 강화
  const performFetch = useCallback(async () => {
    if (!userSelectedLocalCode || userSelectedLocalCode === 'ALL') return;
    setLoading(true);
    try {
      const response = await instance(`${baseURL}/center/${userSelectedLocalCode}`, {
        method: 'GET'
      });

      if (response?.data?.status === 200) {
        setLocalData(response.data.data);
      } else {
        console.error('API response error:', response?.data);
        setLocalData(null);
      }
    } catch (error) {
      console.error('API fetch error:', error);
      setLocalData(null);
    } finally {
      setLoading(false);
    }
  }, [baseURL, userSelectedLocalCode]);

  // mount 혹은 지역 변경 시 호출
  useEffect(() => {
    performFetch();
  }, [performFetch]);

  // 로고 이미지 로드 에러 핸들링
  const handleImageError = e => {
    console.error('Image load error:', e);
    e.target.style.display = 'none';
  };

  return (
    <>
      <Visual category="introduction" title="소개" />
      <Breadcrumbs category="introduction" />
      <Section p="0" pb={isMobile ? 'var(--space-18)' : 'var(--space-35)'} className="local-center">
        <Container size={isMobile && '1'}>
          <PageTitle icon="local" title="지역센터소개" />

          <Box mb={isMobile ? '0' : 'var(--space-35)'} className="local-sort-wrap">
            <Card variant="classic" className="local-sort-card">
              <Box py={!isMobile && 'var(--space-2)'} px={!isMobile && 'var(--space-10)'}>
                <LocalSort hideAll={true} setRegion={setRegion} />
              </Box>
            </Card>
          </Box>

          {!userSelectedLocalCode || userSelectedLocalCode === 'ALL' ? (
            <NoData />
          ) : loading ? (
            <Loading style={{ height: '800px' }} />
          ) : localData ? (
            <Fragment>
              <Flex align="center" justify="between" mb={isMobile ? 'var(--space-6)' : 'var(--space-15)'} px={isMobile && 'var(--space-4)'}>
                <Box className="center-logo">
                  <img
                    src={require(`assets/images/logo/logo-${userSelectedLocalCode}.svg`)}
                    alt={`${localList.find(local => local.code === userSelectedLocalCode)?.name || ''}센터 sw미래채움 로고`}
                    onError={handleImageError}
                  />
                </Box>
                {!isMobile && (
                  <Box>
                    <img src={require('assets/images/icon/icon-school-2.svg').default} alt="" onError={handleImageError} />
                  </Box>
                )}
              </Flex>

              <TabNav.Root className="tabs-list">
                {tabs.map(({ path, label, to }) => (
                  <TabNav.Link key={path} asChild active={pathname === path}>
                    <Link to={to} className="body1" aria-label={pathname === path ? `선택됨 ${label}` : label} aria-live="polite">
                      {label}
                    </Link>
                  </TabNav.Link>
                ))}
              </TabNav.Root>

              {pathname === '/localcenter' && <CenterIntroduction localData={localData?.info} />}
              {pathname === '/localorganization' && <OrganizationChart localData={localData?.organization} />}
              {pathname === '/localmap' && <MapDirections localData={localData?.map} />}
            </Fragment>
          ) : (
            <NoData />
          )}
        </Container>
      </Section>
    </>
  );
}
